import { h, Component, render } from 'preact';
import axios from 'axios';
import { createHashHistory } from 'history';
import linkState from 'linkstate';

const STORE_CODES = [
    [
        'By Region', [
            ['c--USD', 'All US Stores'],
            ['c--EU', 'All European Stores'],
            ['c--GBP', 'All UK Stores'],
            ['c--CAD', 'All Canadian Stores'],
        ]
    ],
    [
        'USA', [
            ['bigcitymusic', 'Big City Music (LA)'],
            ['ctrl-mod', 'Control (Brooklyn)'],
            ['controlvoltage', 'Control Voltage (Portland)'],
            ['detroitmodular', 'Detroit Modular (Detroit)'],
            ['foxtone', 'Foxtone (Minneapolis)'],
            ['meme', 'MeMe Antenna (Brooklyn)'],
            ['midwestmodular', 'Midwest Modular (Minneapolis)'],
            ['nerdaudio', 'Nerd Audio (Chicago)'],
            ['perfectcircuit', 'Perfect Circuit Audio (Burbank)'],
            ['reverb', 'Reverb'],
            ['switched-on', 'Switched On (Austin)'],
            ['synth-city', 'Synth City (Chicago)'],
        ],
    ], [
        'Europe', [
            ['escape', 'Escape from Noise (Stockholm)'],
            ['jamse', 'JAM (Stockholm)'],
            ['loopsbits', 'Loops & Bits (Zürich)'],
            ['martinpas', 'Martin Pas (Turin)'],
            ['noisekitchen', 'Noise Kitchen (Brno)'],
            ['patchpoint', 'Patch Point (Berlin)'],
            ['pyramidsounds', 'Pyramid Sounds (Trondheim)'],
            ['schneidersladen', 'SchneidersLaden (Berlin)'],
            ['soundria', 'Soundria (Wien)'],
            ['synthnetwork', 'The Synthesizer Network (Gothenburg)'],
            ['verkko', 'Verkkokauppa (Helsinki)'],
        ]
    ], [
        'United Kingdom', [
            ['cymru-beats', 'Cymru Beats (Cardiff)'],
            ['london', 'London Modular (London)'],
            ['matttech', 'matttech modular (Manchester)'],
        ]
    ], [
        'Australia', [
            ['foundsound', 'Found Sound (Carlton)'],
        ]
    ], [
        'Canada', [
            ['moogaudio', 'Moog Audio (Toronto, Montreal)'],
            ['technopolis', 'Technopolis (Charlemagne)'],
            ['technosynth', 'TechnoSynth (Montreal)'],
        ]
    ]
];

function getStockStatus (value) {
    switch (value) {
        case 0:
            return 'Out of Stock';
        case 2:
            return 'Pre-order';
        case 3:
            return 'Contact Store';
        case 1:
        default:
            return 'In Stock';
    }
}

const history = createHashHistory({
    hashType: 'slash'
});

const ProductsTable = ({ products }) => (
    <table>
        <thead>
            <tr>
                <th>Item</th>
                <th>Price</th>
                <th>In Stock?</th>
                <th>Cond.</th>
                <th>Store</th>
            </tr>
        </thead>
        <tbody>
            {
                products.map((product, i) => {
                    return (
                        <tr itemscope itemtype="https://schema.org/Product">
                            <td>
                                <a target="_blank"  rel="noopener" href={product.url}>
                                    <span itemprop="name">{product.name}</span>
                                </a>
                            </td>
                            <td itemprop="offers" itemscope itemtype="http://schema.org/Offer">
                                <span
                                    itemprop="itemCondition"
                                    itemprop="itemCondition"
                                    itemtype="https://schema.org/OfferItemCondition"
                                    content={
                                        product.is_used
                                            ? "https://schema.org/UsedCondition"
                                            : "http://schema.org/NewCondition"
                                    }
                                />
                                {
                                    product.stock_status !== 0
                                        ? <span itemprop="availability" href="https://schema.org/InStock" />
                                        : <span itemprop="availability" href="https://schema.org/OutOfStock" />
                                }
                                <span itemprop="priceCurrency" content={product.store.currency_code} />
                                <span itemprop="price" content={product.price}>
                                    {product.formatted_price}
                                </span>
                            </td>
                            <td>
                                {getStockStatus(product.stock_status)}
                            </td>
                            <td>{product.is_used ? 'Used' : 'New'}</td>
                            <td>
                                <a target="_blank" rel="noopener" href={product.store.url}>{product.store.name}</a>
                            </td>
                        </tr>
                    );
                })
            }
        </tbody>
    </table>
);

class Table extends Component {
    constructor (props) {
        super(props);

        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleLoadNext = this.handleLoadNext.bind(this);
        this.handleSearchResults = this.handleSearchResults.bind(this);

        this.state = {
            // table state
            loading: true,
            products: [],
            total: 0,
            currentPage: 1,
            nextPage: null,
            previousPage: null,

            // form state
            query: '',
            storeCode: '',
            condition: '',
            stockStatus: '1',
        };
    }

    componentDidMount () {
        if (history.location.pathname === '/search') {
            const searchState = new URLSearchParams(history.location.search);

            const query = searchState.get('query');

            this.setState({
                query: query,
                stockStatus: searchState.get('stock_status'),
                storeCode: searchState.get('store_code'),
                condition: searchState.get('condition'),
            });

            if (query) {
                gtag('send', 'event', 'Search', query);
            }
        }

        this.fetch().then(data => this.setState(data));
    }

    fetch (pageNumber = 1) {
        const params = {
            page: pageNumber,
        };

        if (this.state.query.length >= 2) {
            params.q = this.state.query;
        }

        params.store_code = this.state.storeCode;
        params.condition = this.state.condition;
        params.stock_status = this.state.stockStatus;

        this.setState({ loading: true });

        return axios.get('/api/v1/products/', { params })
            .then(resp => resp.data)
            .then(data => ({
                products: data.results,
                nextPage: data.next,
                previousPage: data.previous,
                total: data.count,
                loading: false,
            }));
    }

    handleLoadNext () {
        const nextPageNumber = this.state.currentPage + 1;

        this.fetch(nextPageNumber).then(data => {
            const nextState = Object.assign({}, this.state, data);
            nextState.products = this.state.products.slice().concat(data.products);

            this.setState({
                ...nextState,
                currentPage: nextPageNumber,
            })
        });
    }

    handleSubmit (e) {
        e.preventDefault();
        this.fetch().then(this.handleSearchResults);
    }

    handleSearchResults (results) {
        this.setState(results);

        // create query string from state
        const searchState = {
            query: this.state.query,
            stock_status: this.state.stockStatus,
            store_code: this.state.storeCode,
            condition: this.state.condition,
        };

        const bits = new Array(4);
        Object.keys(searchState).map((key, i) => {
            bits[i] = encodeURIComponent(key) + '=' + encodeURIComponent(searchState[key])
        });

        history.push({
            pathname: '/search',
            search: bits.join('&'),
        })
    }

    render () {
        return (
            <div className="content">
                <form onSubmit={this.handleSubmit}>
                    <div className="filters">
                        <div>
                            <input
                                className="search"
                                type="text"
                                value={this.state.query}
                                onChange={linkState(this, 'query')}
                                placeholder="Search for gear by manufacturer or name" />
                        </div>

                        <div>
                            <select onChange={linkState(this, 'condition')}>
                                <option selected={this.state.condition === ''} value="">New and Used</option>
                                <option selected={this.state.condition === 'new'} value="new">New only</option>
                                <option selected={this.state.condition === 'used'} value="used">Used Only</option>
                            </select>
                        </div>

                        <div>
                            <select onChange={linkState(this, 'stockStatus')}>
                                <option selected={this.state.stockStatus === '1'} value="1">In Stock</option>
                                <option selected={this.state.stockStatus === ''} value="">All stock</option>
                            </select>
                        </div>

                        <div>
                            <select onChange={linkState(this, 'storeCode')}>
                                <option selected={this.state.storeCode === ''} value="">All Stores</option>

                                {
                                    STORE_CODES.map(group => (
                                        <optgroup label={group[0]}>
                                            {group[1].map(opt => (
                                                <option
                                                    selected={this.state.storeCode === opt[0]} value={opt[0]}>
                                                    {opt[1]}
                                                </option>
                                            ))}
                                        </optgroup>
                                    ))
                                }
                            </select>
                        </div>

                        <div>
                            <input type="submit" value="Search" />
                        </div>
                    </div>
                </form>

                {
                    this.state.loading === false
                        && this.state.products.length === 0
                        && (
                            <div className="no-results">
                                <p>
                                    <strong>No results 🤯</strong>
                                </p>
                                <p>
                                    Please try adjusting your filters
                                    to include all stock listings.
                                    Some stores ask to be contacted
                                    about availability.
                                </p>
                            </div>
                        )
                }

                {
                    this.state.products.length > 0
                        && <div className="table-container"><ProductsTable products={this.state.products} /></div>
                }

                {
                    this.state.products.length == 0
                        && this.state.loading === true
                        && <div>Loading...</div>
                }

                {
                    this.state.products.length > 0
                        && !!this.state.nextPage
                        && <button className="load-more" onClick={this.handleLoadNext}>Load More</button>
                }
            </div>
        )
    }
}

const mountNode = document.getElementById('inventory-table');
render(<Table />, mountNode);



// WEBPACK FOOTER //
// ./table.js